import React, { useEffect, useMemo, useState } from "react";

const DRAFT_KEY = "assessmentDraft";

export default function AssessmentForm({ config, onSubmit, error: externalError }) {
  const initial = useMemo(() => {
    try {
      const raw = sessionStorage.getItem(DRAFT_KEY);
      return raw ? JSON.parse(raw) : {};
    } catch {
      return {};
    }
  }, []);

  const [answers, setAnswers] = useState(initial.answers || initial || {});
  const [restaurant, setRestaurant] = useState(
    initial.restaurant || { name: "", contactName: "", email: "", location: "" }
  );
  const [consent, setConsent] = useState(!!initial.consent);
  const [marketingOptIn, setMarketingOptIn] = useState(!!initial.marketingOptIn);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  // Persist draft on change
  useEffect(() => {
    try {
      sessionStorage.setItem(
        DRAFT_KEY,
        JSON.stringify({ answers, restaurant, consent, marketingOptIn })
      );
    } catch {}
  }, [answers, restaurant, consent, marketingOptIn]);

  function setValue(id, value) {
    setAnswers(prev => ({ ...prev, [id]: value }));
  }

  function validate() {
    const errs = {};
    // Basic restaurant info validation
    if (!restaurant.name?.trim()) errs["restaurant.name"] = "Restaurant name is required";
    if (!restaurant.contactName?.trim()) errs["restaurant.contactName"] = "Contact name is required";
    if (!restaurant.email?.trim()) {
      errs["restaurant.email"] = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(restaurant.email)) {
      errs["restaurant.email"] = "Enter a valid email";
    }
    if (!consent) errs["consent"] = "Consent is required";

    for (const cat of (config.categories || [])) {
      for (const q of cat.questions || []) {
        const val = answers[q.id];
        // Required validation (supports arrays for multi-select)
        if (q.required && (
          val === undefined || val === null || val === "" || (Array.isArray(val) && val.length === 0)
        )) {
          errs[q.id] = "This field is required";
          continue;
        }
        if (val !== undefined && val !== null && val !== "") {
          if (q.type === "number_times_per_year") {
            const n = Number(val);
            if (Number.isNaN(n)) errs[q.id] = "Must be a number";
            if (q.min != null && n < q.min) errs[q.id] = `Must be >= ${q.min}`;
            if (q.max != null && n > q.max) errs[q.id] = `Must be <= ${q.max}`;
          }
        }
      }
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      await onSubmit?.(answers, restaurant, { consent, marketingOptIn });
    } finally {
      setSubmitting(false);
    }
  }

  function renderQuestion(q) {
    const val = answers[q.id] ?? "";
    const err = errors[q.id];
    const common = {
      id: q.id,
      name: q.id,
      onChange: (e) => setValue(q.id, e.target.value),
      "aria-invalid": !!err,
      "aria-describedby": err ? `${q.id}-err` : undefined,
      "aria-labelledby": `${q.id}-label`,
    };

    switch (q.type) {
      case "likert_1_5": {
        const left = q.scale_left_label || "Low";
        const right = q.scale_right_label || "High";
        return (
          <div className="likert-scale">
            <div className="d-flex align-items-center flex-wrap gap-3" role="radiogroup" aria-labelledby={`${q.id}-label`}>
              <span className="text-muted small me-1">{left}</span>
              {[1,2,3,4,5].map(n => (
                <label key={n} className="me-2">
                  <input
                    type="radio"
                    name={q.id}
                    value={String(n)}
                    checked={String(val) === String(n)}
                    onChange={(e) => setValue(q.id, e.target.value)}
                  />
                  <span className="ms-1">{n}</span>
                </label>
              ))}
              <span className="text-muted small ms-1">{right}</span>
            </div>
          </div>
        );
      }
      case "yes_no": {
        return (
          <div role="radiogroup" aria-labelledby={`${q.id}-label`}>
            {[
              { k: "yes", label: "Yes" },
              { k: "no", label: "No" },
            ].map(opt => (
              <label key={opt.k} className="me-3">
                <input
                  type="radio"
                  name={q.id}
                  value={opt.k}
                  checked={val === opt.k}
                  onChange={(e) => setValue(q.id, e.target.value)}
                /> {opt.label}
              </label>
            ))}
          </div>
        );
      }
      case "number_days":
      case "number_times_per_year": {
        return (
          <input
            {...common}
            type="number"
            inputMode="numeric"
            min={q.min}
            max={q.max}
            value={val}
            placeholder={undefined}
          />
        );
      }
      case "ordinal_n_r_s_o": {
        const opts = [
          { k: "never", label: "Never" },
          { k: "rarely", label: "Rarely" },
          { k: "sometimes", label: "Sometimes" },
          { k: "often", label: "Often" },
          { k: "always", label: "Always" },
        ];
        return (
          <div role="radiogroup" aria-labelledby={`${q.id}-label`}>
            {opts.map(opt => (
              <label key={opt.k} className="me-3">
                <input
                  type="radio"
                  name={q.id}
                  value={opt.k}
                  checked={val === opt.k}
                  onChange={(e) => setValue(q.id, e.target.value)}
                /> {opt.label}
              </label>
            ))}
          </div>
        );
      }
      case "percent_bracket":
      case "hours_bracket": {
        const opts = (q.options || []).map((o) =>
          typeof o === "object" ? { id: String(o.id), label: o.label } : { id: String(o), label: String(o) }
        );
        return (
          <div role="radiogroup" aria-labelledby={`${q.id}-label`}>
            {opts.map((opt) => (
              <label key={opt.id} className="me-3">
                <input
                  type="radio"
                  name={q.id}
                  value={opt.id}
                  checked={String(val) === opt.id}
                  onChange={(e) => setValue(q.id, e.target.value)}
                /> {opt.label}
              </label>
            ))}
          </div>
        );
      }
      case "mcq_multi": {
        const selected = Array.isArray(val) ? val : [];
        function toggle(opt) {
          setAnswers(prev => {
            const curr = Array.isArray(prev[q.id]) ? prev[q.id] : [];
            let next;
            if (curr.includes(opt)) {
              next = curr.filter(x => x !== opt);
            } else {
              // If selecting "None", clear others
              if (opt.toLowerCase() === "none") {
                next = [opt];
              } else {
                next = curr.filter(x => x.toLowerCase() !== "none").concat(opt);
              }
            }
            return { ...prev, [q.id]: next };
          });
        }
        return (
          <div role="group" aria-labelledby={`${q.id}-label`}>
            {(q.options || []).map(opt => (
              <label key={opt} className="d-inline-flex align-items-center me-3">
                <input
                  type="checkbox"
                  className="me-1"
                  checked={selected.includes(opt)}
                  onChange={() => toggle(opt)}
                />
                {opt}
              </label>
            ))}
          </div>
        );
      }
      case "mcq_single": {
        const opts = (q.options || []).map((o) =>
          typeof o === "object" ? { id: String(o.id), label: o.label } : { id: String(o), label: String(o) }
        );
        return (
          <div role="radiogroup" aria-labelledby={`${q.id}-label`}>
            {opts.map((opt) => (
              <label key={opt.id} className="me-3">
                <input
                  type="radio"
                  name={q.id}
                  value={opt.id}
                  checked={String(val) === opt.id}
                  onChange={(e) => setValue(q.id, e.target.value)}
                /> {opt.label}
              </label>
            ))}
          </div>
        );
      }
      default:
        return <input {...common} type="text" value={val} />;
    }
  }

  return (
    <form onSubmit={handleSubmit} noValidate>
      {/* Top: Restaurant details */}
      <section className="mb-4 assessment-card p-3 p-md-4">
        <h2 className="h5 mb-3 section-title">Your Restaurant</h2>
        <div className="row g-3">
          <div className="col-md-6">
            <label className="form-label" htmlFor="assessment-restaurant-name">Restaurant Name <span className="text-danger">*</span></label>
            <input
              id="assessment-restaurant-name"
              className="form-control"
              value={restaurant.name}
              onChange={(e) => setRestaurant(r => ({ ...r, name: e.target.value }))}
              aria-invalid={!!errors["restaurant.name"]}
              aria-describedby={errors["restaurant.name"] ? "assessment-restaurant-name-error" : undefined}
            />
            {errors["restaurant.name"] && <div id="assessment-restaurant-name-error" className="text-danger small" role="alert">{errors["restaurant.name"]}</div>}
          </div>
          <div className="col-md-6">
            <label className="form-label" htmlFor="assessment-contact-name">Contact Name <span className="text-danger">*</span></label>
            <input
              id="assessment-contact-name"
              className="form-control"
              value={restaurant.contactName}
              onChange={(e) => setRestaurant(r => ({ ...r, contactName: e.target.value }))}
              aria-invalid={!!errors["restaurant.contactName"]}
              aria-describedby={errors["restaurant.contactName"] ? "assessment-contact-name-error" : undefined}
            />
            {errors["restaurant.contactName"] && <div id="assessment-contact-name-error" className="text-danger small" role="alert">{errors["restaurant.contactName"]}</div>}
          </div>
          <div className="col-md-6">
            <label className="form-label" htmlFor="assessment-email">Email <span className="text-danger">*</span></label>
            <input
              id="assessment-email"
              type="email"
              className="form-control"
              value={restaurant.email}
              onChange={(e) => setRestaurant(r => ({ ...r, email: e.target.value }))}
              aria-invalid={!!errors["restaurant.email"]}
              aria-describedby={errors["restaurant.email"] ? "assessment-email-error" : undefined}
            />
            {errors["restaurant.email"] && <div id="assessment-email-error" className="text-danger small" role="alert">{errors["restaurant.email"]}</div>}
          </div>
          <div className="col-md-6">
            <label className="form-label" htmlFor="assessment-location">Location</label>
            <input
              id="assessment-location"
              className="form-control"
              placeholder="City, State"
              value={restaurant.location}
              onChange={(e) => setRestaurant(r => ({ ...r, location: e.target.value }))}
            />
          </div>
        </div>
        <div className="form-check mt-3">
          <input
            className="form-check-input"
            type="checkbox"
            id="consent"
            checked={consent}
            onChange={(e) => setConsent(e.target.checked)}
            aria-invalid={!!errors["consent"]}
            aria-describedby={errors["consent"] ? "assessment-consent-error" : undefined}
          />
          <label className="form-check-label" htmlFor="consent">
            I consent to receive a one-time email with my results. I have read and agree to the
            {" "}
            <a href="/terms" target="_blank" rel="noopener noreferrer">Terms of Service</a>
            {" and "}
            <a href="/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>.
          </label>
          {errors["consent"] && <div id="assessment-consent-error" className="text-danger small" role="alert">{errors["consent"]}</div>}
        </div>
        <p className="small text-muted mt-2 mb-0">
          Results are estimates only and not guarantees. See our
          {" "}
          <a href="/disclaimer" target="_blank" rel="noopener noreferrer">Disclaimer</a>.
        </p>
        <div className="form-check mt-3">
          <input
            className="form-check-input"
            type="checkbox"
            id="marketingOptIn"
            checked={marketingOptIn}
            onChange={(e) => setMarketingOptIn(e.target.checked)}
          />
          <label className="form-check-label" htmlFor="marketingOptIn">
            (Optional) Yes, I’d like to receive occasional tips, research, and special offers from Speak Your Menu. I understand I can unsubscribe at any time.
          </label>
        </div>
        {externalError && <div className="alert alert-danger mt-3" role="alert">{externalError}</div>}
      </section>

      {(config.categories || []).map(cat => (
        <section key={cat.id} className="mb-4 assessment-card p-3 p-md-4">
          <h2 className="h5 mb-3 section-title">{cat.label}</h2>
          <div className="vstack gap-3">
            {(cat.questions || []).map(q => (
              <div key={q.id}>
                <p id={`${q.id}-label`} className="form-label fw-semibold mb-2">
                  {q.label} {q.required && <span className="text-danger">*</span>}
                </p>
                {renderQuestion(q)}
                {errors[q.id] && (
                  <div id={`${q.id}-err`} className="text-danger small mt-1" role="alert">{errors[q.id]}</div>
                )}
              </div>
            ))}
          </div>
        </section>
      ))}
      <div className="d-flex gap-2 assessment-actions mt-3">
        <button type="submit" className="btn btn-wine-fill" disabled={submitting}>
          {submitting ? "Submitting..." : "Submit"}
        </button>
        <button type="button" className="btn btn-outline-secondary" onClick={() => {
          try { sessionStorage.removeItem(DRAFT_KEY); } catch {}
          setAnswers({});
          setRestaurant({ name: "", contactName: "", email: "", location: "" });
          setConsent(false);
          setMarketingOptIn(false);
          setErrors({});
        }}>
          Clear Draft
        </button>
      </div>
    </form>
  );
}
